import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ModuleState } from '@redux/modules/module'

const watchEntityKeys = ['agencyContacts']

type AgencyContactFilters = {
  agency_id?: number,
}

type UseAgencyContactsOptions = {
  entityKey?: string,
  filters?: AgencyContactFilters,
  performHttpRequests?: boolean,
}

const useAgencyContacts = (options: UseAgencyContactsOptions = {}) => {
  const { entityKey, filters, performHttpRequests } = options
  const { agency_id: filterAgencyId } = filters || {}

  const {
    updatedEntities: { agencyContacts: agencyContactsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { loading: loadingAgencyContacts } = useReduxAction(
    'agencyContacts',
    'loadAgencyContacts',
    {
      entityKey,
      ...filters,
    },
    [filters, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const agencyContacts = useSelector(reduxState => reduxState.entities.agencyContacts)

  const filteredAgencyContacts = useMemo(() => {
    const filtered = Object.values(agencyContacts).filter((agencyContact) => {
      const { agency_id } = agencyContact

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)

      return matchAgencyId
    })

    return sortArrayBy(filtered, 'asc', 'name')
  }, [agencyContactsUpdatedAt, JSON.stringify(filters)])

  const hasFilteredAgencyContacts = !!filteredAgencyContacts.length

  return {
    filteredAgencyContacts,
    hasFilteredAgencyContacts,
    loading: loadingAgencyContacts,
  }
}

export default useAgencyContacts
